import type { AlternateRouteDefinition, MapDefinition, NpcDifficulty, RivalStyle } from './types.js';
import { routeSurfaceProfile } from './map-identity.js';
import {
  clamp,
  getTrackGeometry,
  signedAngleDifference,
  trackProgressAtIndex,
  type BakedRoadGeometry,
} from './track.js';

export interface RacingLineSample {
  x: number;
  y: number;
  z: number;
  tangentX: number;
  tangentY: number;
  tangentZ: number;
  heading: number;
  progress: number;
  width: number;
  offset: number;
  curvature: number;
  targetSpeed: number;
  sourceSegment: number;
}

export interface RacingLine {
  readonly id: string;
  readonly closed: boolean;
  readonly length: number;
  readonly samples: readonly RacingLineSample[];
}

export interface RacingPlan {
  readonly main: RacingLine;
  readonly alternateRoutes: ReadonlyMap<string, RacingLine>;
  readonly estimatedLapTime: number;
}

export interface RacingLineProjection {
  readonly sample: RacingLineSample;
  readonly progress: number;
  readonly distance: number;
}

const racingPlanCache = new WeakMap<MapDefinition, RacingPlan>();

/**
 * Generates one deterministic line from the same baked road used by physics
 * and rendering. The apex phase starts outside, clips inside, and opens on
 * exit; a backwards braking pass turns curvature/elevation into a speed plan.
 */
export function getRacingPlan(map: MapDefinition): RacingPlan {
  const cached = racingPlanCache.get(map);
  if (cached) return cached;
  const geometry = getTrackGeometry(map);
  const main = buildRacingLine(map, 'main', geometry.main);
  const alternateRoutes = new Map<string, RacingLine>();
  for (const route of map.features.alternateRoutes ?? []) {
    const road = geometry.alternateRoutes.get(route.id);
    if (!road) continue;
    alternateRoutes.set(route.id, buildRacingLine(map, route.id, road, route));
  }
  const plan: RacingPlan = {
    main,
    alternateRoutes,
    estimatedLapTime: estimateRacingLineTime(main),
  };
  racingPlanCache.set(map, plan);
  return plan;
}

/** Returns the nearest immutable line sample without allocating per AI tick. */
export function racingLineSampleAt(line: RacingLine, progressValue: number): RacingLineSample {
  const count = line.samples.length;
  if (count === 0) return EMPTY_LINE_SAMPLE;
  const progress = line.closed ? mod(progressValue, 1) : clamp(progressValue, 0, 1);
  const index = Math.min(count - 1, Math.round(progress * (line.closed ? count : count - 1)) % count);
  return line.samples[index] ?? line.samples[0] ?? EMPTY_LINE_SAMPLE;
}

/** Projects onto a precomputed line; used only for committed branch tracking. */
export function projectToRacingLine(
  line: RacingLine,
  x: number,
  z: number,
  hintProgress?: number,
): RacingLineProjection {
  let best = line.samples[0] ?? EMPTY_LINE_SAMPLE;
  let bestDistanceSq = Number.POSITIVE_INFINITY;
  const sampleCount = line.samples.length;
  if (Number.isFinite(hintProgress) && sampleCount > 0) {
    const centre = Math.round(clamp(Number(hintProgress), 0, 1) * (line.closed ? sampleCount : sampleCount - 1));
    const sampleSpacing = line.length / Math.max(1, line.closed ? sampleCount : sampleCount - 1);
    const radius = clamp(Math.ceil(70 / Math.max(0.5, sampleSpacing)), 16, 96);
    for (let offset = -radius; offset <= radius; offset += 1) {
      const index = line.closed ? mod(centre + offset, sampleCount) : centre + offset;
      if (index < 0 || index >= sampleCount) continue;
      const sample = line.samples[index] ?? EMPTY_LINE_SAMPLE;
      const distanceSq = (sample.x - x) ** 2 + (sample.z - z) ** 2;
      if (distanceSq >= bestDistanceSq) continue;
      best = sample;
      bestDistanceSq = distanceSq;
    }
  } else {
    for (let index = 0; index < sampleCount; index += 1) {
      const sample = line.samples[index] ?? EMPTY_LINE_SAMPLE;
      const distanceSq = (sample.x - x) ** 2 + (sample.z - z) ** 2;
      if (distanceSq >= bestDistanceSq) continue;
      best = sample;
      bestDistanceSq = distanceSq;
    }
  }
  return {
    sample: best,
    progress: best.progress,
    distance: Math.sqrt(bestDistanceSq),
  };
}

export function racingLineLookahead(line: RacingLine, progress: number, distance: number): RacingLineSample {
  return racingLineSampleAt(line, progress + distance / Math.max(1, line.length));
}

/** Minimum planned speed over the next distance, used for anticipatory brake. */
export function racingLineSpeedAhead(line: RacingLine, progress: number, distance: number): number {
  const steps = clamp(Math.ceil(distance / 4), 2, 18);
  let minimum = Number.POSITIVE_INFINITY;
  for (let index = 0; index <= steps; index += 1) {
    minimum = Math.min(minimum, racingLineLookahead(line, progress, distance * index / steps).targetSpeed);
  }
  return minimum;
}

/** Peak absolute curvature ahead, allowing vehicle-specific corner limits. */
export function racingLineCurvatureAhead(line: RacingLine, progress: number, distance: number): number {
  const steps = clamp(Math.ceil(distance / 4), 2, 18);
  let maximum = 0;
  for (let index = 0; index <= steps; index += 1) {
    maximum = Math.max(
      maximum,
      Math.abs(racingLineLookahead(line, progress, distance * index / steps).curvature),
    );
  }
  return maximum;
}

export function estimateRacingLineTime(line: RacingLine): number {
  if (line.samples.length < 2) return 0;
  const segmentLength = line.length / (line.closed ? line.samples.length : line.samples.length - 1);
  let seconds = 0;
  const segmentCount = line.closed ? line.samples.length : line.samples.length - 1;
  for (let index = 0; index < segmentCount; index += 1) {
    const first = line.samples[index] ?? EMPTY_LINE_SAMPLE;
    const second = line.samples[(index + 1) % line.samples.length] ?? first;
    seconds += segmentLength / Math.max(4, (first.targetSpeed + second.targetSpeed) * 0.5);
  }
  return seconds;
}

/** Personality/position-driven route choice with no runtime randomness. */
export function shouldRivalUseRoute(
  style: RivalStyle,
  route: AlternateRouteDefinition,
  place: number,
  lap: number,
  seed: number,
): boolean {
  const value = hashUnit(`${style}:${route.id}:${lap}:${seed.toFixed(3)}`);
  if (route.kind === 'shortcut') {
    const baseChance = {
      sprinter: 0.94,
      bully: place > 2 ? 0.82 : 0.58,
      trickster: 0.88,
      drift: place > 3 ? 0.64 : 0.34,
      grip: place > 2 ? 0.52 : 0.22,
    }[style];
    const riskAdjustment = route.risk === 'extreme'
      ? ({ sprinter: -0.04, bully: 0.03, trickster: 0.02, drift: -0.02, grip: -0.2 } as const)[style]
      : route.risk === 'technical'
        ? ({ sprinter: 0, bully: 0.02, trickster: 0.06, drift: 0.05, grip: -0.06 } as const)[style]
        : ({ sprinter: 0.02, bully: -0.04, trickster: 0.02, drift: -0.05, grip: 0.08 } as const)[style];
    const rewardAdjustment = clamp(((route.reward ?? 8) - 9) * 0.012, -0.05, 0.1);
    return value < clamp(baseChance + riskAdjustment + rewardAdjustment, 0.05, 0.98);
  }
  const baseChance = {
    trickster: 0.9,
    drift: 0.72,
    bully: 0.2,
    sprinter: place === 1 ? 0.08 : 0.16,
    grip: 0.12,
  }[style];
  const spectacleAdjustment = route.risk === 'extreme' ? 0.04 : route.risk === 'technical' ? 0.08 : 0;
  return value < clamp(baseChance + spectacleAdjustment, 0.04, 0.96);
}

/** Small, transparent pace compression keeps solo fields in fighting distance
 * without teleporting rivals or granting impossible corner speed. A positive
 * gap means the human is ahead of this rival. */
export function rivalPaceScale(
  gapMetres: number,
  difficulty: NpcDifficulty,
  raceSeconds: number,
  focusedDuel = false,
): number {
  if (!Number.isFinite(gapMetres) || raceSeconds < 10) return 1;
  const maximum = difficulty === 'rookie' ? 0.045 : difficulty === 'sport' ? 0.032 : 0.02;
  const active = clamp((Math.abs(gapMetres) - 32) / 128, 0, 1);
  const duel = focusedDuel ? 0.006 : 0;
  const correction = Math.min(maximum + duel, active * maximum + duel * active);
  return gapMetres >= 0 ? 1 + correction : 1 - correction;
}

export function distanceToRouteEntry(map: MapDefinition, progress: number, route: AlternateRouteDefinition): number {
  const fromProgress = trackProgressAtIndex(map, route.fromTrackIndex);
  return mod(fromProgress - progress, 1) * getTrackGeometry(map).main.length;
}

function buildRacingLine(
  map: MapDefinition,
  id: string,
  road: BakedRoadGeometry,
  route?: AlternateRouteDefinition,
): RacingLine {
  const count = road.samples.length;
  if (count === 0) return { id, closed: road.closed, length: road.length, samples: [] };
  const stepLength = road.length / Math.max(1, road.closed ? count : count - 1);
  const rawCurvature = road.samples.map((_sample, index) => roadCurvature(road, index, stepLength));
  const curvature = rawCurvature.map((_value, index) => weightedNeighbourhood(rawCurvature, index, road.closed));
  const entrySamples = Math.max(2, Math.round(20 / Math.max(0.5, stepLength)));
  const exitSamples = Math.max(2, Math.round(13 / Math.max(0.5, stepLength)));
  let offsets = road.samples.map((sample, index) => {
    const current = Math.tanh((curvature[index] ?? 0) * 19);
    const future = Math.tanh((curvature[indexAt(index + entrySamples, count, road.closed)] ?? 0) * 19);
    const previous = Math.tanh((curvature[indexAt(index - exitSamples, count, road.closed)] ?? 0) * 19);
    const phase = clamp(current * 1.28 - future * 0.56 - previous * 0.3, -1, 1);
    const usableHalfWidth = Math.max(0, sample.width * 0.5 - 1.65);
    const localCurvature = Math.max(
      Math.abs(curvature[index] ?? 0),
      Math.abs(curvature[indexAt(index + entrySamples, count, road.closed)] ?? 0),
      Math.abs(curvature[indexAt(index - exitSamples, count, road.closed)] ?? 0),
    );
    // An inside offset larger than a tight bend's radius folds back on itself.
    // Keep switchback lines continuous even on intentionally angular maps.
    const curvatureSafeOffset = 0.38 / Math.max(0.001, localCurvature);
    const endpointFade = road.closed ? 1 : Math.sin(Math.PI * sample.progress) ** 0.7;
    return phase * Math.min(usableHalfWidth, curvatureSafeOffset) * 0.58 * endpointFade;
  });
  const smoothingRadius = clamp(Math.round(8 / Math.max(0.5, stepLength)), 3, 16);
  for (let pass = 0; pass < 3; pass += 1) {
    const previous = offsets;
    offsets = previous.map((value, index) => {
      if (!road.closed && (index === 0 || index === count - 1)) return 0;
      let weightedOffset = 0;
      let weightTotal = 0;
      for (let neighbour = -smoothingRadius; neighbour <= smoothingRadius; neighbour += 1) {
        const resolved = indexAt(index + neighbour, count, road.closed);
        if (resolved < 0 || resolved >= count) continue;
        const weight = smoothingRadius + 1 - Math.abs(neighbour);
        weightedOffset += (previous[resolved] ?? value) * weight;
        weightTotal += weight;
      }
      const maximum = Math.max(0, (road.samples[index]?.width ?? 0) * 0.5 - 1.55);
      return clamp(weightedOffset / Math.max(1, weightTotal), -maximum, maximum);
    });
  }

  const samples: RacingLineSample[] = road.samples.map((sample, index) => {
    const offset = offsets[index] ?? 0;
    const rightX = sample.tangentZ;
    const rightZ = -sample.tangentX;
    return {
      x: sample.x + rightX * offset,
      y: sample.y,
      z: sample.z + rightZ * offset,
      tangentX: sample.tangentX,
      tangentY: sample.tangentY,
      tangentZ: sample.tangentZ,
      heading: Math.atan2(sample.tangentX, sample.tangentZ),
      progress: sample.progress,
      width: sample.width,
      offset,
      curvature: 0,
      targetSpeed: 78,
      sourceSegment: sample.sourceSegment,
    };
  });
  smoothLinePositions(samples, road.closed, stepLength);
  updateLineTangentsAndCurvature(samples, road.closed, stepLength);
  buildSpeedProfile(map, samples, road.closed, stepLength, route);
  return { id, closed: road.closed, length: road.length, samples };
}

function smoothLinePositions(samples: RacingLineSample[], closed: boolean, stepLength: number): void {
  const radius = clamp(Math.round(7 / Math.max(0.5, stepLength)), 3, 14);
  for (let pass = 0; pass < 3; pass += 1) {
    const previous = samples.map((sample) => ({ x: sample.x, z: sample.z }));
    for (let index = 0; index < samples.length; index += 1) {
      if (!closed && (index === 0 || index === samples.length - 1)) continue;
      let x = 0;
      let z = 0;
      let weightTotal = 0;
      for (let offset = -radius; offset <= radius; offset += 1) {
        const resolved = indexAt(index + offset, samples.length, closed);
        if (resolved < 0 || resolved >= samples.length) continue;
        const weight = radius + 1 - Math.abs(offset);
        x += (previous[resolved]?.x ?? samples[index]!.x) * weight;
        z += (previous[resolved]?.z ?? samples[index]!.z) * weight;
        weightTotal += weight;
      }
      samples[index]!.x = x / Math.max(1, weightTotal);
      samples[index]!.z = z / Math.max(1, weightTotal);
    }
  }
}

function updateLineTangentsAndCurvature(samples: RacingLineSample[], closed: boolean, stepLength: number): void {
  for (let index = 0; index < samples.length; index += 1) {
    const before = samples[indexAt(index - 1, samples.length, closed)] ?? samples[index]!;
    const current = samples[index]!;
    const after = samples[indexAt(index + 1, samples.length, closed)] ?? current;
    const dx = after.x - before.x;
    const dy = after.y - before.y;
    const dz = after.z - before.z;
    const length = Math.max(1e-8, Math.hypot(dx, dy, dz));
    current.tangentX = dx / length;
    current.tangentY = dy / length;
    current.tangentZ = dz / length;
    current.heading = Math.atan2(current.tangentX, current.tangentZ);
  }
  for (let index = 0; index < samples.length; index += 1) {
    const before = samples[indexAt(index - 1, samples.length, closed)] ?? samples[index]!;
    const after = samples[indexAt(index + 1, samples.length, closed)] ?? samples[index]!;
    samples[index]!.curvature = signedAngleDifference(after.heading, before.heading) / Math.max(0.5, stepLength * 2);
  }
}

function buildSpeedProfile(
  map: MapDefinition,
  samples: RacingLineSample[],
  closed: boolean,
  stepLength: number,
  route?: AlternateRouteDefinition,
): void {
  const weatherGrip = map.weather === 'rain' ? 0.82 : map.weather === 'sand' ? 0.88 : map.weather === 'snow' ? 0.8 : map.weather === 'haze' ? 0.96 : 1;
  const branchSurface = routeSurfaceProfile(route);
  for (const sample of samples) {
    const curveSpeed = Math.sqrt((8.6 * weatherGrip) / Math.max(0.0007, Math.abs(sample.curvature)));
    const downhillCaution = 1 - clamp(-sample.tangentY * 0.42, 0, 0.12);
    let target = clamp(curveSpeed * downhillCaution, 12, 78);
    if (route?.kind === 'shortcut') target *= 0.95;
    if (route?.kind === 'loop') target *= 0.91;
    if (route) target *= branchSurface.speed * Math.sqrt(branchSurface.grip);
    for (const ramp of map.features.ramps) {
      if (cyclicSegmentDistance(sample.sourceSegment, ramp.trackIndex, map.track.length) <= 1) {
        target = clamp(target, 15, 34);
      }
    }
    for (const loop of map.features.stuntLoops ?? []) {
      if (cyclicSegmentDistance(sample.sourceSegment, loop.trackIndex, map.track.length) <= 1) {
        target = Math.max(target, loop.minSpeed + 2.5);
      }
    }
    sample.targetSpeed = target;
  }

  // Work backwards around the lap so every sample already knows how early a
  // driver must brake for the next corner, including reduced downhill braking.
  const passes = closed ? 4 : 1;
  for (let pass = 0; pass < passes; pass += 1) {
    for (let index = samples.length - 1; index >= 0; index -= 1) {
      if (!closed && index === samples.length - 1) continue;
      const current = samples[index]!;
      const next = samples[(index + 1) % samples.length] ?? current;
      const braking = clamp(10.5 * weatherGrip + current.tangentY * 9.81, 5.8, 13.5);
      current.targetSpeed = Math.min(
        current.targetSpeed,
        Math.sqrt(next.targetSpeed ** 2 + 2 * braking * stepLength),
      );
    }
  }
}

function roadCurvature(road: BakedRoadGeometry, index: number, stepLength: number): number {
  const before = road.samples[indexAt(index - 1, road.samples.length, road.closed)] ?? road.samples[index]!;
  const after = road.samples[indexAt(index + 1, road.samples.length, road.closed)] ?? road.samples[index]!;
  const beforeHeading = Math.atan2(before.tangentX, before.tangentZ);
  const afterHeading = Math.atan2(after.tangentX, after.tangentZ);
  return signedAngleDifference(afterHeading, beforeHeading) / Math.max(0.5, stepLength * 2);
}

function weightedNeighbourhood(values: readonly number[], index: number, closed: boolean): number {
  const weights = [1, 2, 3, 2, 1] as const;
  let total = 0;
  let weightTotal = 0;
  for (let offset = -2; offset <= 2; offset += 1) {
    const resolved = indexAt(index + offset, values.length, closed);
    if (resolved < 0 || resolved >= values.length) continue;
    const weight = weights[offset + 2] ?? 1;
    total += (values[resolved] ?? 0) * weight;
    weightTotal += weight;
  }
  return total / Math.max(1, weightTotal);
}

function indexAt(index: number, count: number, closed: boolean): number {
  return closed ? mod(index, count) : clamp(index, 0, count - 1);
}

function cyclicSegmentDistance(first: number, second: number, count: number): number {
  const direct = Math.abs(first - second);
  return Math.min(direct, count - direct);
}

function hashUnit(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return (hash >>> 0) / 0xffff_ffff;
}

function mod(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}

const EMPTY_LINE_SAMPLE: RacingLineSample = {
  x: 0,
  y: 0,
  z: 0,
  tangentX: 0,
  tangentY: 0,
  tangentZ: 1,
  heading: 0,
  progress: 0,
  width: 0,
  offset: 0,
  curvature: 0,
  targetSpeed: 12,
  sourceSegment: 0,
};
